import {useEffect, useState} from "react";
import { PdfReadySignal } from "./components/pdfReadySignal";
import PdfComponent from "./components/pdfComponent";
import {PdfKompetenceHeaderComponent} from "./pdfHeaders/pdfKompetenceHeaderComponent";
import PdfRow from "./components/pdfRow";
import Title from "../titels/title";
import useLogbogApi from "../../hooks/useLogbogApi";
import useStamdataApi from "../../hooks/useStamdataApi";
import {PdfKompetenceHeaderModel} from "./models/pdfKompetenceHeaderModel";
import {KompetenceEvalueringModel} from "../../sharedmodels/evaluering/kompetenceEvalueringModel";
import {useRouteMatch} from "react-router-dom";
import {useFetchWithErrorHandlingPdf} from "./hooks/useFetchWithErrorHandlingPdf";
import { fetchKompetenceHeader } from "./helpers/pdfFetchers";
import HtmlHeading from "../titels/HtmlHeadings";
import {Localizer} from "../../../infrastructure/localization/localizer";
import {svarReadTypeCompareMap} from "../../../pages/kompetencerPage/readSvar/svarReadTypeCompareMap";
import {KvSkemaConstants} from "../../../pages/kompetencerPage/evaluering/KbuSkemaConstants";
import {BesvaretAf} from "../../../pages/kompetencerPage/readSvar/besvaretAf";

export type KompetencevurderingPdfParams = {
    brugerId: string
    laegekompetenceEvalueringId: string
    brugerMaalbeskrivelseId: string,
};

const KompetenceEvalueringPdf = () => {

    const [kompetencevurdering, setKompetencevurdering] = useState<KompetenceEvalueringModel>();
    const [headerData, setHeaderData] = useState<PdfKompetenceHeaderModel>();
    const [dataLoaded, setDataLoaded] = useState(false);

    const {
        laegekompetenceEvalueringId,
        brugerMaalbeskrivelseId,
        brugerId
    } = useRouteMatch<KompetencevurderingPdfParams>().params;

    const setError = useFetchWithErrorHandlingPdf();
    const { evalueringApi, logbogUserInfoApi } = useLogbogApi();
    const { stamdataMaalbeskrivelseApi, stamdataSpecialeApi } = useStamdataApi();

    const pdfRowMargin = "margin-top-l";
    const pdfRenderId = "pdfRenderId";

    useEffect(() => {
        const fetchData = async () => {
            const [vurdering, header] = await Promise.all([
                evalueringApi.getKompetenceEvaluering(laegekompetenceEvalueringId),
                fetchKompetenceHeader(brugerId, brugerMaalbeskrivelseId, { stamdataMaalbeskrivelseApi, stamdataSpecialeApi, logbogUserInfoApi }),
            ]);
            setKompetencevurdering(vurdering);
            setHeaderData(header);
            setDataLoaded(true);
        };
        fetchData().catch(setError);
    }, [laegekompetenceEvalueringId, brugerMaalbeskrivelseId, brugerId]);

    return (
        <PdfComponent>
            <PdfComponent.PageBreakAfter key={kompetencevurdering?.id}>

                {headerData &&
                    <PdfKompetenceHeaderComponent
                        brugerId={brugerId}
                        data={headerData}
                    />
                }

                <PdfComponent.BlockItem className={"card shadow-none"}>
                    <PdfComponent.PageBreakAfter>
                        <PdfComponent.CardBody>
                            <PdfRow>
                                <PdfRow.Col>
                                    <Title title={kompetencevurdering?.titel} heading={HtmlHeading.H4}/>
                                </PdfRow.Col>
                            </PdfRow>

                            <PdfRow.Col width={12}>
                                <Title title={Localizer.global_kompetence()}
                                       subTitle={kompetencevurdering?.kompetenceTitel}
                                       largeSubTitle nonSubtleSubTitle/>
                            </PdfRow.Col>

                            <PdfRow className={pdfRowMargin}>
                                <PdfRow.Col width={4}>
                                    <Title title={Localizer.global_createdBy()}
                                           subTitle={`${kompetencevurdering?.createdByUser?.firstName} ${kompetencevurdering?.createdByUser?.lastName}`}
                                           largeSubTitle nonSubtleSubTitle/>
                                </PdfRow.Col>
                                <PdfRow.Col width={4}>
                                    <Title title={Localizer.dato()}
                                           subTitle={kompetencevurdering?.indsendtDato ? kompetencevurdering?.indsendtDato.dateWithoutTimeFormat(true) : ""}
                                           largeSubTitle nonSubtleSubTitle/>
                                </PdfRow.Col>
                            </PdfRow>

                            <PdfRow className={pdfRowMargin}>
                                <PdfRow.Col width={12}>
                                    <Title title={Localizer.Kompetencevurdering()}
                                           subTitle={kompetencevurdering?.tekst}
                                           largeSubTitle nonSubtleSubTitle/>
                                </PdfRow.Col>
                            </PdfRow>

                            <PdfRow className={pdfRowMargin}>
                                <PdfRow.Col width={12}>
                                    <Title title={Localizer.VejledersVurdering()}>
                                        <div className="flex-column margin-bottom-m">
                                            {kompetencevurdering?.sektioner.filter(s => s.includedInProgress).map(sektion => sektion.spoergsmaal.map(spoergsmaal =>
                                                    <PdfComponent.NonBreakableItem key={spoergsmaal.id + sektion.id}>
                                                        <div className="margin-bottom-m">
                                                            <>
                                                                {spoergsmaal.isFeedback &&
                                                                    <h5>{Localizer.global_feedback()}</h5>}
                                                                {spoergsmaal.isFeedback ?
                                                                    <i><p>{spoergsmaal?.titel}</p></i>
                                                                    :
                                                                    <>
                                                                        <p>{spoergsmaal?.titel}</p>
                                                                        <p className={"subtle"}>{spoergsmaal.tekst}</p>
                                                                    </>
                                                                }
                                                            </>
                                                            {spoergsmaal.evalueringSvar?.map(es => {
                                                                const Component = svarReadTypeCompareMap[spoergsmaal.spoergsmaalTypeEnum];
                                                                return Component ?
                                                                    <div className={"d-flex align-items-center"}>
                                                                        <Component spoergsmaal={spoergsmaal}
                                                                                   evalueringSvar={es}
                                                                                   excludeAnswerText={KvSkemaConstants.InitialKbuSkemaIds.includes(kompetencevurdering.evalueringsSkemaId)}
                                                                                   renderId={pdfRenderId}/>
                                                                        {es.stempel &&
                                                                                <BesvaretAf stempel={es.stempel}></BesvaretAf>
                                                                        }
                                                                    </div>
                                                                    : <></>;
                                                            })}
                                                        </div>
                                                        <div className="border margin-bottom-m"/>
                                                    </PdfComponent.NonBreakableItem>
                                                )
                                            )}
                                        </div>
                                    </Title>
                                </PdfRow.Col>
                            </PdfRow>
                        </PdfComponent.CardBody>
                    </PdfComponent.PageBreakAfter>
                </PdfComponent.BlockItem>
            </PdfComponent.PageBreakAfter>
            <PdfReadySignal when={dataLoaded} />
        </PdfComponent>
    );
}

export default KompetenceEvalueringPdf;
